/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { ClinicalAmcLicenseDetailResponse } from '../models/clinical-amc-license-detail-response';
import { ClinicalLicenseRequestListResponse } from '../models/clinical-license-request-list-response';
import { ClinicalLicenseRequestResponse } from '../models/clinical-license-request-response';
import { ClinicalLicenseRequestStatus } from '../models/clinical-license-request-status';
import { RejectClinicalLicenseRequest } from '../models/reject-clinical-license-request';
import { SaveClinicalLicenseRequest } from '../models/save-clinical-license-request';

/**
 * Applicant license submissions and Training Department approval.
 */
@Injectable({ providedIn: 'root' })
export class ClinicalLicenseRequestsService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `saveMyClinicalLicense()` */
  static readonly SaveMyClinicalLicensePath =
    '/api/v1/clinical-privileges/licenses/me';

  /**
   * Save the caller's license details.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `saveMyClinicalLicense()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  saveMyClinicalLicense$Response(
    params: {
      body: SaveClinicalLicenseRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<ClinicalLicenseRequestResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ClinicalLicenseRequestsService.SaveMyClinicalLicensePath,
      'put'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ClinicalLicenseRequestResponse>;
        })
      );
  }

  /**
   * Save the caller's license details.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `saveMyClinicalLicense$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  saveMyClinicalLicense(
    params: {
      body: SaveClinicalLicenseRequest;
    },
    context?: HttpContext
  ): Observable<ClinicalLicenseRequestResponse> {
    return this.saveMyClinicalLicense$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<ClinicalLicenseRequestResponse>
        ): ClinicalLicenseRequestResponse => r.body
      )
    );
  }

  /** Path part for operation `listMyClinicalLicenseHistory()` */
  static readonly ListMyClinicalLicenseHistoryPath =
    '/api/v1/clinical-privileges/licenses/me/history';

  /**
   * License submission history for the caller.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listMyClinicalLicenseHistory()` instead.
   *
   * This method doesn't expect any request body.
   */
  listMyClinicalLicenseHistory$Response(
    params?: {},
    context?: HttpContext
  ): Observable<StrictHttpResponse<ClinicalLicenseRequestListResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ClinicalLicenseRequestsService.ListMyClinicalLicenseHistoryPath,
      'get'
    );
    if (params) {
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ClinicalLicenseRequestListResponse>;
        })
      );
  }

  /**
   * License submission history for the caller.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `listMyClinicalLicenseHistory$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listMyClinicalLicenseHistory(
    params?: {},
    context?: HttpContext
  ): Observable<ClinicalLicenseRequestListResponse> {
    return this.listMyClinicalLicenseHistory$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<ClinicalLicenseRequestListResponse>
        ): ClinicalLicenseRequestListResponse => r.body
      )
    );
  }

  /** Path part for operation `getClinicalLicenseForAmc()` */
  static readonly GetClinicalLicenseForAmcPath =
    '/api/v1/clinical-privileges/licenses/amcs/{amcUserId}';

  /**
   * One AMC license with current details and full history.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getClinicalLicenseForAmc()` instead.
   *
   * This method doesn't expect any request body.
   */
  getClinicalLicenseForAmc$Response(
    params: {
      amcUserId: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<ClinicalAmcLicenseDetailResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ClinicalLicenseRequestsService.GetClinicalLicenseForAmcPath,
      'get'
    );
    if (params) {
      rb.path('amcUserId', params.amcUserId, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ClinicalAmcLicenseDetailResponse>;
        })
      );
  }

  /**
   * One AMC license with current details and full history.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getClinicalLicenseForAmc$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getClinicalLicenseForAmc(
    params: {
      amcUserId: number;
    },
    context?: HttpContext
  ): Observable<ClinicalAmcLicenseDetailResponse> {
    return this.getClinicalLicenseForAmc$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<ClinicalAmcLicenseDetailResponse>
        ): ClinicalAmcLicenseDetailResponse => r.body
      )
    );
  }

  /** Path part for operation `listClinicalLicenseRequests()` */
  static readonly ListClinicalLicenseRequestsPath =
    '/api/v1/clinical-privileges/license-requests';

  /**
   * Training Department license request lists.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listClinicalLicenseRequests()` instead.
   *
   * This method doesn't expect any request body.
   */
  listClinicalLicenseRequests$Response(
    params: {
      /**
       * Filter by status. Pass multiple values to select more than one status.
       */
      status: Array<ClinicalLicenseRequestStatus>;

      /**
       * When true, keep only expired approved rows. When false, keep only not-expired rows. Omit for the full status list.
       */
      expired?: boolean;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<ClinicalLicenseRequestListResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ClinicalLicenseRequestsService.ListClinicalLicenseRequestsPath,
      'get'
    );
    if (params) {
      rb.query('status', params.status, { style: 'form', explode: true });
      rb.query('expired', params.expired, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ClinicalLicenseRequestListResponse>;
        })
      );
  }

  /**
   * Training Department license request lists.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `listClinicalLicenseRequests$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listClinicalLicenseRequests(
    params: {
      /**
       * Filter by status. Pass multiple values to select more than one status.
       */
      status: Array<ClinicalLicenseRequestStatus>;

      /**
       * When true, keep only expired approved rows. When false, keep only not-expired rows. Omit for the full status list.
       */
      expired?: boolean;
    },
    context?: HttpContext
  ): Observable<ClinicalLicenseRequestListResponse> {
    return this.listClinicalLicenseRequests$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<ClinicalLicenseRequestListResponse>
        ): ClinicalLicenseRequestListResponse => r.body
      )
    );
  }

  /** Path part for operation `approveClinicalLicenseRequest()` */
  static readonly ApproveClinicalLicenseRequestPath =
    '/api/v1/clinical-privileges/license-requests/{id}/approve';

  /**
   * Approve a waiting license request.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `approveClinicalLicenseRequest()` instead.
   *
   * This method doesn't expect any request body.
   */
  approveClinicalLicenseRequest$Response(
    params: {
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<ClinicalLicenseRequestResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ClinicalLicenseRequestsService.ApproveClinicalLicenseRequestPath,
      'post'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ClinicalLicenseRequestResponse>;
        })
      );
  }

  /**
   * Approve a waiting license request.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `approveClinicalLicenseRequest$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  approveClinicalLicenseRequest(
    params: {
      id: number;
    },
    context?: HttpContext
  ): Observable<ClinicalLicenseRequestResponse> {
    return this.approveClinicalLicenseRequest$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<ClinicalLicenseRequestResponse>
        ): ClinicalLicenseRequestResponse => r.body
      )
    );
  }

  /** Path part for operation `rejectClinicalLicenseRequest()` */
  static readonly RejectClinicalLicenseRequestPath =
    '/api/v1/clinical-privileges/license-requests/{id}/reject';

  /**
   * Reject a waiting license request.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `rejectClinicalLicenseRequest()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  rejectClinicalLicenseRequest$Response(
    params: {
      id: number;
      body: RejectClinicalLicenseRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<ClinicalLicenseRequestResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ClinicalLicenseRequestsService.RejectClinicalLicenseRequestPath,
      'post'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ClinicalLicenseRequestResponse>;
        })
      );
  }

  /**
   * Reject a waiting license request.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `rejectClinicalLicenseRequest$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  rejectClinicalLicenseRequest(
    params: {
      id: number;
      body: RejectClinicalLicenseRequest;
    },
    context?: HttpContext
  ): Observable<ClinicalLicenseRequestResponse> {
    return this.rejectClinicalLicenseRequest$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<ClinicalLicenseRequestResponse>
        ): ClinicalLicenseRequestResponse => r.body
      )
    );
  }
}
